<script setup lang="ts">
import {
  formatUsd,
  JOB_PACK_APPLICATIONS,
  JOB_PACK_DAYS,
  JOB_PACK_LEAD_LINE,
  JOB_PACK_STACK,
  MARKET_ANCHORS,
  separateToolsMonthly,
} from '~/constants/pricing'
import type { JobPackLead } from '~/constants/pricing'

/**
 * The Job Search Pack as an offer, in the order a buyer decides: what it gets
 * them, what it costs next to the market, what is in it, the button, and only
 * then the fine print (comparison and what stays free) behind a toggle.
 *
 * Two layouts:
 * - `offer`: a standalone card (generator result, download page) with its own
 *   heading and price box; the page's button goes in the `cta` slot.
 * - `card`: inside something that already shows the name and price (pricing
 *   grid, checkout modal, premium page), so only the market line and the list.
 *
 * Every number comes from the visitor's price list (usePricing) and
 * MARKET_ANCHORS, so a price change reaches every copy of this. The market's
 * prices stay in dollars, as they are charged.
 */
const props = withDefaults(defineProps<{
  layout?: 'offer' | 'card'
  /** Puts the line this upsell is about first, e.g. the thing a limit just blocked. */
  lead?: JobPackLead
  /** The market comparison. Off where the pack is already bought (claim page). */
  showAnchor?: boolean
  /** `offer` layout: a headline about this buyer (their role, their match score) instead of the general one. */
  headline?: string
  /** `offer` layout: the line under that headline. */
  sub?: string
}>(), {
  layout: 'card',
  lead: undefined,
  showAnchor: true,
  headline: undefined,
  sub: undefined,
})

const { t } = useI18n()

const lines = computed(() => {
  const first = props.lead ? JOB_PACK_LEAD_LINE[props.lead] : undefined
  return first ? [first, ...JOB_PACK_STACK.filter(line => line !== first)] : [...JOB_PACK_STACK]
})

const { prices, format } = usePricing().pricing.value
const packPrice = format(prices.jobPack)
const separately = `$${separateToolsMonthly()}`
const perApplication = format(prices.jobPack / JOB_PACK_APPLICATIONS)
const builders = MARKET_ANCHORS.cvBuilderEvery4Weeks
const comparison = [
  { key: 'builders', price: `${formatUsd(builders.low)}–${formatUsd(builders.high)}`, period: 'every4Weeks' },
  { key: 'jobMatch', price: formatUsd(MARKET_ANCHORS.jobMatchToolMonthly), period: 'month' },
  { key: 'website', price: formatUsd(MARKET_ANCHORS.personalWebsiteMonthly), period: 'monthFrom' },
] as const
</script>

<template>
  <div>
    <template v-if="layout === 'offer'">
      <div class="text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
        {{ t('premium.jobPack.title') }}
      </div>
      <div class="display-heading display-heading-4 mt-1">{{ headline || t('premium.jobPack.stack.headline') }}</div>
      <p class="mt-2 !text-base">{{ sub || t('premium.jobPack.stack.sub') }}</p>
    </template>

    <!--
      The market line and the price as two rows of the same shape, so the eye
      compares them. Not a strike-through: we never sold the pack for $90.
    -->
    <div
      v-if="layout === 'offer' || showAnchor"
      class="rounded-[0.625rem] bg-surface p-4 dark:bg-gray-900"
      :class="layout === 'offer' ? 'my-5' : 'mb-6'"
    >
      <div v-if="showAnchor" class="flex items-baseline justify-between gap-4">
        <div class="min-w-0 text-sm text-body dark:text-gray-300">{{ t('premium.jobPack.stack.separatelyLabel') }}</div>
        <div class="shrink-0 text-right font-title text-lg font-bold tabular-nums text-muted dark:text-gray-400">
          {{ t('premium.jobPack.stack.separatelyPrice', { price: separately }) }}
        </div>
      </div>
      <template v-if="layout === 'offer'">
        <div v-if="showAnchor" class="my-3 h-px w-full bg-line dark:bg-gray-700" />
        <div class="flex items-baseline justify-between gap-4">
          <div class="min-w-0 font-title text-base font-bold text-ink dark:text-white">{{ t('premium.jobPack.title') }}</div>
          <div class="shrink-0 font-title text-3xl font-bold tabular-nums text-ink dark:text-white">{{ packPrice }}</div>
        </div>
        <div class="mt-1 text-right text-xs text-muted dark:text-gray-400">
          {{ t('premium.jobPack.stack.once', { days: JOB_PACK_DAYS }) }}
        </div>
      </template>
      <div v-if="showAnchor" class="mt-3 text-sm text-body dark:text-gray-300">
        {{ t('premium.jobPack.stack.perApplication', { count: JOB_PACK_APPLICATIONS, price: perApplication }) }}
      </div>
    </div>

    <ul class="space-y-3">
      <li v-for="line in lines" :key="line" class="flex items-start gap-3">
        <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl leading-none text-accent" />
        <div>
          <div class="font-title text-base font-semibold text-ink dark:text-white">
            {{ t(`premium.jobPack.stack.lines.${line}.title`) }}
          </div>
          <div class="text-sm text-body dark:text-gray-300">
            {{ t(`premium.jobPack.stack.lines.${line}.detail`, { days: JOB_PACK_DAYS }) }}
          </div>
        </div>
      </li>
    </ul>

    <div v-if="$slots.cta" class="mt-6">
      <slot name="cta" />
    </div>

    <!-- The fine print: which tools the comparison means, and what stays free -->
    <details v-if="showAnchor" class="group mt-4 text-sm">
      <summary class="cursor-pointer list-none font-semibold text-ink dark:text-white [&::-webkit-details-marker]:hidden">
        <i class="bi bi-chevron-right mr-1 inline-block text-xs transition-transform group-open:rotate-90" aria-hidden="true" />
        {{ t('premium.jobPack.stack.details.summary') }}
      </summary>
      <div class="mt-3 space-y-3 text-body dark:text-gray-300">
        <ul class="space-y-2">
          <li v-for="row in comparison" :key="row.key">
            <div>{{ t(`premium.jobPack.stack.details.${row.key}`) }}</div>
            <div class="font-semibold tabular-nums text-ink dark:text-white">
              {{ t(`premium.jobPack.stack.details.${row.period}`, { price: row.price }) }}
            </div>
          </li>
        </ul>
        <p class="!text-xs text-muted dark:text-gray-400">{{ t('premium.jobPack.stack.details.footnote') }}</p>
        <div>
          <div class="font-semibold text-ink dark:text-white">{{ t('premium.jobPack.stack.details.freeTitle') }}</div>
          <p class="mt-1 !text-sm">{{ t('premium.jobPack.stack.details.freeText') }}</p>
        </div>
      </div>
    </details>
  </div>
</template>
